import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { autoUpdate, computePosition, flip, offset, shift, size } from '@floating-ui/dom';
import { IntelVariable } from '../types';
import { VariablesPopover } from './VariablesPopover';

interface PositionedVariablesPopoverProps {
  /** Element the popover anchors to and repositions against on scroll/resize. */
  anchorEl: HTMLElement;
  variables: IntelVariable[];
  pending?: boolean;
  /** Open straight into this variable's editor + scroll/pulse it. */
  initialEditId?: number | null;
  onPopulate: (variableId: number, value: number, currency: boolean) => void;
  onAddWorkspaceVariable: () => void;
  onAddAssessmentVariable: () => void;
  /** Override the leading row icon per variable (e.g. per-type icons in the scenario form). */
  renderIcon?: (variable: IntelVariable) => React.ReactNode;
  /** When set, clicking a row picks that variable (the `#` insert flow). */
  onSelect?: (variable: IntelVariable) => void;
  onClose: () => void;
}

/**
 * The shared "variables popup" for every anchored surface (scenario-form chip, AI-wizard formula
 * pills): portals to <body> so the host modal's overflow/transform can't clip it, positions against
 * the anchor with floating-ui (sizing the card to the space up to the screen frame — the list
 * scrolls within), and dismisses on outside-click / Escape. The list itself is `VariablesPopover`.
 */
export const PositionedVariablesPopover: React.FC<PositionedVariablesPopoverProps> = ({
  anchorEl,
  variables,
  pending,
  initialEditId,
  onPopulate,
  onAddWorkspaceVariable,
  onAddAssessmentVariable,
  renderIcon,
  onSelect,
  onClose,
}) => {
  const floatingRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const el = floatingRef.current;
    if (!el) return;
    const update = () =>
      computePosition(anchorEl, el, {
        strategy: 'fixed',
        placement: 'bottom-start',
        middleware: [
          offset(6),
          flip(),
          shift({ padding: 8 }),
          size({
            padding: 8,
            apply: ({ availableHeight }) => setMaxHeight(Math.round(Math.max(180, availableHeight))),
          }),
        ],
      }).then(({ x, y }) => {
        if (floatingRef.current) Object.assign(floatingRef.current.style, { left: `${x}px`, top: `${y}px` });
      });
    return autoUpdate(anchorEl, el, update);
  }, [anchorEl]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (floatingRef.current?.contains(t) || anchorEl.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchorEl, onClose]);

  // High enough to clear a host modal's close ✕ (Modal.tsx CloseIcon, z-index 1000000).
  return createPortal(
    <div ref={floatingRef} style={{ position: 'fixed', top: 0, left: 0, zIndex: 1000001 }}>
      <VariablesPopover
        variables={variables}
        pending={!!pending}
        maxHeight={maxHeight}
        initialEditId={initialEditId}
        onPopulate={onPopulate}
        onAddWorkspaceVariable={onAddWorkspaceVariable}
        onAddAssessmentVariable={onAddAssessmentVariable}
        renderIcon={renderIcon}
        onSelect={onSelect}
      />
    </div>,
    document.body,
  );
};
